import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SignatureCanvas from 'react-signature-canvas';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../../shared/AppSelect';

const SignaturePad = ({ isOpen, onClose, onSave }) => {
  const sigCanvas = useRef();
  const containerRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [penColor, setPenColor] = useState('#000000');
  const [penWidth, setPenWidth] = useState(2);
  const [canvasSize, setCanvasSize] = useState({ width: 700, height: 240 });

  // Ensure the pad always appears above transformed/overflow parents by portaling to body
  const [portalEl] = useState(() => {
    if (typeof document === 'undefined') return null;
    const el = document.createElement('div');
    el.setAttribute('id', 'signature-pad-portal');
    return el;
  });

  useEffect(() => {
    if (!portalEl || typeof document === 'undefined') return;
    document.body.appendChild(portalEl);
    return () => {
      if (portalEl.parentNode) portalEl.parentNode.removeChild(portalEl);
    };
  }, [portalEl]);

  useEffect(() => {
    if (isOpen && sigCanvas.current) {
      sigCanvas.current.clear();
      setIsEmpty(true);
      // Compute responsive size
      requestAnimationFrame(() => {
        const maxWidth = Math.min(window.innerWidth * 0.96, 720);
        const width = containerRef.current ? Math.min(containerRef.current.clientWidth - 24, maxWidth) : maxWidth;
        const height = Math.max(200, Math.min(280, Math.round(width / 3)));
        setCanvasSize({ width, height });
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => {
      const maxWidth = Math.min(window.innerWidth * 0.96, 720);
      const width = containerRef.current ? Math.min(containerRef.current.clientWidth - 24, maxWidth) : maxWidth;
      const height = Math.max(200, Math.min(280, Math.round(width / 3)));
      setCanvasSize({ width, height });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen]);

  const handleClear = () => {
    sigCanvas.current?.clear();
    setIsEmpty(true);
  };

  const handleSave = () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      alert('Please draw your signature first');
      return;
    }
    let signatureData = '';
    try {
      if (typeof sigCanvas.current.getTrimmedCanvas === 'function') {
        signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      } else {
        signatureData = sigCanvas.current.getCanvas().toDataURL('image/png');
      }
    } catch (err) {
      signatureData = sigCanvas.current.getCanvas().toDataURL('image/png');
    }
    onSave?.(signatureData);
    onClose?.();
  };

  const handleBegin = () => setIsEmpty(false);

  if (!isOpen || !portalEl) return null;

  const modal = (
    <div className="fixed inset-0 z-[1000] bg-black bg-opacity-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div ref={containerRef} className="w-full max-w-[720px] text-left bg-white shadow-xl rounded-lg max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-200">
          <div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900">Create Digital Signature</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Draw using mouse or finger. Adjust color/width below.</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" title="Close">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Canvas */}
        <div className="flex-1 p-3 sm:p-4 overflow-y-auto">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 sm:p-3 bg-gray-50">
            <SignatureCanvas
              ref={sigCanvas}
              canvasProps={{
                width: canvasSize.width,
                height: canvasSize.height,
                className: 'signature-canvas bg-white rounded border',
                style: { width: '100%', height: `${canvasSize.height}px` }
              }}
              backgroundColor="white"
              penColor={penColor}
              minWidth={penWidth}
              maxWidth={penWidth + 1}
              onBegin={handleBegin}
            />
          </div>

          {/* Controls */}
          <div className="mt-3 space-y-3">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Color:</label>
                <div className="flex space-x-2">
                  {['#000000', '#0066cc', '#cc0000', '#006600'].map(color => (
                    <button key={color} onClick={() => setPenColor(color)} className={`w-6 h-6 rounded-full border-2 ${penColor === color ? 'border-gray-800' : 'border-gray-300'}`} style={{ backgroundColor: color }} title={`Select ${color}`} />
                  ))}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Width:</label>
                <AppSelect
                  value={penWidth}
                  onChange={(e) => setPenWidth(parseInt(e.target.value))}
                  aria-label="Pen width"
                  sheetTitle="Pen width"
                  options={[
                    { value: 1, label: 'Thin' },
                    { value: 2, label: 'Normal' },
                    { value: 3, label: 'Thick' },
                    { value: 4, label: 'Extra Thick' },
                  ]}
                  triggerClassName={`${APP_SELECT_FILTER_TRIGGER} w-auto min-w-[7rem] text-sm border-gray-300 rounded px-2 py-1 min-h-[36px]`}
                />
              </div>
              <div className="ml-auto">
                <button onClick={handleClear} className="px-3 py-2 text-gray-600 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium">🗑️ Clear</button>
              </div>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs sm:text-sm text-blue-700">Tip: Use a trackpad or touchscreen for smoother strokes. You can also upload a scanned signature image from the Documents section instead.</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={isEmpty} className={`px-5 py-2 rounded-lg font-medium transition-colors ${isEmpty ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>Save Signature</button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, portalEl);
};

export default SignaturePad;
